import * as React from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';

import { AuthLayout } from './routes/auth-layout';
import { ProtectedRoute } from './routes/protected-route';
import { RouteErrorPage } from './components/route-error-page';

// ── Lazy page imports (route-based code splitting) ────────────

const AnalyticsPage          = React.lazy(() => import('./routes/protected/analytics-page').then((m) => ({ default: m.AnalyticsPage })));
const AutomationBuilderPage  = React.lazy(() => import('./routes/protected/automation-builder-page').then((m) => ({ default: m.AutomationBuilderPage })));
const AutomationDetailPage   = React.lazy(() => import('./routes/protected/automation-detail-page').then((m) => ({ default: m.AutomationDetailPage })));
const AutomationsPage        = React.lazy(() => import('./routes/protected/automations-page').then((m) => ({ default: m.AutomationsPage })));
const CampaignDetailPage     = React.lazy(() => import('./routes/protected/campaign-detail-page').then((m) => ({ default: m.CampaignDetailPage })));
const CampaignWizardPage     = React.lazy(() => import('./routes/protected/campaign-wizard').then((m) => ({ default: m.CampaignWizardPage })));
const CampaignsPage          = React.lazy(() => import('./routes/protected/campaigns-page').then((m) => ({ default: m.CampaignsPage })));
const ContactDetailPage      = React.lazy(() => import('./routes/protected/contact-detail-page').then((m) => ({ default: m.ContactDetailPage })));
const ContactsPage           = React.lazy(() => import('./routes/protected/contacts-page').then((m) => ({ default: m.ContactsPage })));
const ConnectorsPage         = React.lazy(() => import('./routes/protected/connectors-page').then((m) => ({ default: m.ConnectorsPage })));
const CreateSegmentPage      = React.lazy(() => import('./routes/protected/create-segment-page').then((m) => ({ default: m.CreateSegmentPage })));
const DashboardPage          = React.lazy(() => import('./routes/protected/dashboard-page').then((m) => ({ default: m.DashboardPage })));
const ProductDetailPage      = React.lazy(() => import('./routes/protected/product-detail-page').then((m) => ({ default: m.ProductDetailPage })));
const ProductsPage           = React.lazy(() => import('./routes/protected/products-page').then((m) => ({ default: m.ProductsPage })));
const SegmentDetailPage      = React.lazy(() => import('./routes/protected/segment-detail-page').then((m) => ({ default: m.SegmentDetailPage })));
const SegmentsPage           = React.lazy(() => import('./routes/protected/segments-page').then((m) => ({ default: m.SegmentsPage })));
const SettingsPage           = React.lazy(() => import('./routes/protected/settings-page').then((m) => ({ default: m.SettingsPage })));
const TemplatesPage          = React.lazy(() => import('./routes/protected/templates-page').then((m) => ({ default: m.TemplatesPage })));
const ShopifyOAuthCallbackPage   = React.lazy(() => import('./routes/protected/shopify-oauth-callback-page').then((m) => ({ default: m.ShopifyOAuthCallbackPage })));
const WhatsAppCallbackPage   = React.lazy(() => import('./routes/protected/whatsapp-callback-page').then((m) => ({ default: m.WhatsAppCallbackPage })));
const WhatsAppComposerPage   = React.lazy(() => import('./routes/protected/whatsapp-composer-page').then((m) => ({ default: m.WhatsAppComposerPage })));
const PushComposerPage       = React.lazy(() => import('./routes/protected/push-composer-page').then((m) => ({ default: m.PushComposerPage })));
const PopupBuilderPage       = React.lazy(() => import('./routes/protected/popup-builder-page').then((m) => ({ default: m.PopupBuilderPage })));
const PopupsPage             = React.lazy(() => import('./routes/protected/popups-page').then((m) => ({ default: m.PopupsPage })));
const LoginPage              = React.lazy(() => import('./routes/public/login-page').then((m) => ({ default: m.LoginPage })));
const RegisterPage           = React.lazy(() => import('./routes/public/register-page').then((m) => ({ default: m.RegisterPage })));
const InvitationAcceptPage   = React.lazy(() => import('./routes/public/invitation-accept-page').then((m) => ({ default: m.InvitationAcceptPage })));
const OnboardingPage         = React.lazy(() => import('./routes/public/onboarding-page').then((m) => ({ default: m.OnboardingPage })));
const SenderEmailVerificationPage = React.lazy(() => import('./routes/public/sender-email-verification-page').then((m) => ({ default: m.SenderEmailVerificationPage })));

// ── Route-level Suspense fallback ────────────────────────────

function PageFallback(): React.JSX.Element {
  return (
    <div
      className="flex h-screen items-center justify-center bg-bg-base"
      role="status"
      aria-live="polite"
      aria-label="Loading page"
    >
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-default border-t-transparent" />
    </div>
  );
}

function SuspenseRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <React.Suspense fallback={<PageFallback />}>{children}</React.Suspense>;
}

function PublicOnlyRoute(): React.JSX.Element {
  return (
    <SuspenseRoute>
      <Outlet />
    </SuspenseRoute>
  );
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  // Shopify OAuth callback — must be outside ProtectedRoute (merchants arrive unauthenticated)
  { path: '/connectors/shopify/callback', element: <SuspenseRoute><ShopifyOAuthCallbackPage /></SuspenseRoute>, errorElement: <RouteErrorPage /> },
  {
    element: <PublicOnlyRoute />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/invitations/:token/accept', element: <InvitationAcceptPage /> },
      { path: '/onboarding', element: <OnboardingPage /> },
      { path: '/verify-sender-email', element: <SenderEmailVerificationPage /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/automations/new/builder', element: <SuspenseRoute><AutomationBuilderPage /></SuspenseRoute> },
      { path: '/automations/:id/builder', element: <SuspenseRoute><AutomationBuilderPage /></SuspenseRoute> },
      { path: '/popups/:id/edit', element: <SuspenseRoute><PopupBuilderPage /></SuspenseRoute> },
      { path: '/campaigns/new', element: <SuspenseRoute><CampaignWizardPage /></SuspenseRoute> },
      {
        element: <AuthLayout />,
        children: [
          { path: '/dashboard', element: <SuspenseRoute><DashboardPage /></SuspenseRoute> },
          { path: '/contacts', element: <SuspenseRoute><ContactsPage /></SuspenseRoute> },
          { path: '/connectors', element: <SuspenseRoute><ConnectorsPage /></SuspenseRoute> },
          { path: '/contacts/:id', element: <SuspenseRoute><ContactDetailPage /></SuspenseRoute> },
          { path: '/segments', element: <SuspenseRoute><SegmentsPage /></SuspenseRoute> },
          { path: '/segments/new', element: <SuspenseRoute><CreateSegmentPage /></SuspenseRoute> },
          { path: '/segments/:id', element: <SuspenseRoute><SegmentDetailPage /></SuspenseRoute> },
          { path: '/campaigns', element: <SuspenseRoute><CampaignsPage /></SuspenseRoute> },
          { path: '/campaigns/:id', element: <SuspenseRoute><CampaignDetailPage /></SuspenseRoute> },
          { path: '/products', element: <SuspenseRoute><ProductsPage /></SuspenseRoute> },
          { path: '/products/:id', element: <SuspenseRoute><ProductDetailPage /></SuspenseRoute> },
          { path: '/automations', element: <SuspenseRoute><AutomationsPage /></SuspenseRoute> },
          { path: '/automations/:id', element: <SuspenseRoute><AutomationDetailPage /></SuspenseRoute> },
          { path: '/popups', element: <SuspenseRoute><PopupsPage /></SuspenseRoute> },
          { path: '/templates', element: <SuspenseRoute><TemplatesPage /></SuspenseRoute> },
          { path: '/templates/new/email', element: <SuspenseRoute><TemplatesPage /></SuspenseRoute> },
          { path: '/templates/compose/email', element: <Navigate to="/templates" replace /> },
          { path: '/templates/compose/whatsapp', element: <SuspenseRoute><WhatsAppComposerPage /></SuspenseRoute> },
          { path: '/templates/compose/push', element: <SuspenseRoute><PushComposerPage /></SuspenseRoute> },
          { path: '/email-builder/new', element: <SuspenseRoute><TemplatesPage /></SuspenseRoute> },
          { path: '/analytics', element: <SuspenseRoute><AnalyticsPage /></SuspenseRoute> },
          { path: '/settings/whatsapp/callback', element: <SuspenseRoute><WhatsAppCallbackPage /></SuspenseRoute> },
          { path: '/settings', element: <Navigate to="/settings/general" replace /> },
          { path: '/settings/:section', element: <SuspenseRoute><SettingsPage /></SuspenseRoute> },
          { path: '/settings/:section/templates', element: <SuspenseRoute><SettingsPage /></SuspenseRoute> },
        ],
      },
    ],
  },
  { path: '/', element: <Navigate to="/dashboard" replace /> },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);
